// ============================================
// FULL RESTORE INFINITE SCROLL WITH BACK BUTTON FIX
// ============================================

// Prevent browser's default scroll restoration
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

// ============================================
// STATE MANAGEMENT WITH ERROR HANDLING
// ============================================

const STORAGE_KEY = 'infiniteScrollState';
const MAX_STATE_AGE = 30 * 60 * 1000; // 30 minutes

// Safely get DOM elements
function getScrollElements() {
  return {
    grid: document.querySelector('.load-more-grid'),
    currentPageInput: document.querySelector("[data-current-page]"),
    nextUrlInput: document.querySelector("[data-next-url]"),
    totalPagesInput: document.querySelector("[data-total-pages]")
  };
}

function saveState() {
  try {
    const { grid, currentPageInput, nextUrlInput } = getScrollElements();
    
    if (!grid || !currentPageInput) return;
    
    const state = {
      gridContent: grid.innerHTML,
      currentPage: currentPageInput.value,
      nextUrl: nextUrlInput ? nextUrlInput.value : '',
      scrollPos: window.scrollY,
      timestamp: Date.now(),
      savedUrl: window.location.pathname + window.location.search 
    };
    
    // Validate state before saving
    if (!state.currentPage || !state.savedUrl) {
      console.warn('Invalid state data, skipping save');
      return;
    }
    
    // Save to sessionStorage with error handling
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (storageError) {
      console.warn('SessionStorage quota exceeded:', storageError);
      return;
    }
    
    // Also save minimal state to history API
    history.replaceState(
      { page: currentPageInput.value }, 
      '', 
      window.location.pathname + window.location.search
    );
  } catch (error) {
    console.error('Error saving scroll state:', error);
  }
}

function restoreState() {
  try {
    const savedStateStr = sessionStorage.getItem(STORAGE_KEY);
    
    if (!savedStateStr) return false;
    
    let state;
    try {
      state = JSON.parse(savedStateStr);
    } catch (parseError) {
      console.error('Invalid state data:', parseError);
      sessionStorage.removeItem(STORAGE_KEY);
      return false;
    }
    
    // Validate state structure
    if (!state || !state.timestamp || !state.currentPage) {
      sessionStorage.removeItem(STORAGE_KEY);
      return false;
    }
    
    // Check if state is fresh
    if (Date.now() - state.timestamp > MAX_STATE_AGE) {
      sessionStorage.removeItem(STORAGE_KEY);
      return false;
    }
    
    // Verify URL matches
    const currentUrl = window.location.pathname + window.location.search;
    if (state.savedUrl && state.savedUrl !== currentUrl) {
      sessionStorage.removeItem(STORAGE_KEY);
      return false;
    }
    
    // Verify grid content isn't empty or too large
    if (!state.gridContent || state.gridContent.length > 10000000) {
      console.warn('Invalid grid content');
      sessionStorage.removeItem(STORAGE_KEY);
      return false;
    }
    
    const { grid, currentPageInput, nextUrlInput } = getScrollElements();
    
    if (!grid || !currentPageInput) return false;
    
    // Restore grid content
    try {
      grid.innerHTML = state.gridContent;
    } catch (domError) {
      console.error('Error restoring grid content:', domError);
      return false;
    }
    
    // Restore page state
    currentPageInput.value = state.currentPage;
    currentPageInput.dataset.currentPage = state.currentPage;
    
    if (nextUrlInput && state.nextUrl) {
      nextUrlInput.value = state.nextUrl;
      nextUrlInput.dataset.nextUrl = state.nextUrl;
    }
    
    // Restore scroll position after a short delay
    setTimeout(() => {
      try {
        window.scrollTo(0, state.scrollPos);
      } catch (scrollError) {
        console.warn('Error restoring scroll position:', scrollError);
      }
    }, 100);
    
    // Re-initialize color swatches if available
    if (typeof colorSwatches === 'function') {
      try {
        colorSwatches();
      } catch (swatchError) {
        console.warn('Error initializing color swatches:', swatchError);
      }
    }
    
    // Clear the saved state after restoration
    sessionStorage.removeItem(STORAGE_KEY);
    
    return true;
  } catch (error) {
    console.error('Unexpected error restoring state:', error);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
    return false;
  }
}

// ============================================
// LOAD ITEMS FUNCTION (ENHANCED)
// ============================================

function loadItems(r) {
  let totalPages = parseInt(document.querySelector("[data-total-pages]").value, 10);
  let currentPage = parseInt(document.querySelector("[data-current-page]").value, 10);
  
  let nextPage = currentPage + 1;
  let pageAfterNext = currentPage + 2;
  
  let nextUrl = document.querySelector("[data-next-url]").value.replace(/page=[0-9]+/, "page=" + nextPage);
  let urlForUpdate = document.querySelector("[data-next-url]").value.replace(/page=[0-9]+/, "page=" + pageAfterNext);
  
  // Update current page before fetch
  document.querySelector("[data-current-page]").value = nextPage;
  
  // Disable button and show loading state
  r.setAttribute("disabled", "");
  r.classList.add("loading");
  
  fetch(nextUrl)
    .then(response => {
      if (response.ok) return response.text();
      throw new Error("Network response was not ok");
    })
    .then(html => {
      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newContent = doc.querySelector(".load-more-grid").innerHTML;
      
      // Insert new content
      document.querySelector(".load-more-grid").insertAdjacentHTML("beforeend", newContent);
      
      // Re-initialize color swatches
      if (typeof colorSwatches === 'function') {
        colorSwatches();
      }
      
      // Save state after successful load
      saveState();
    })
    .catch(error => {
      console.error("Error loading items:", error);
      
      // Revert page number on error
      document.querySelector("[data-current-page]").value = currentPage;
      
      r.removeAttribute("disabled");
      r.classList.remove("loading");
    })
    .finally(() => {
      if (nextPage <= totalPages) {
        // Update infinite scroll data if exists
        const infiniteScrollData = document.querySelector(".infinite-scroll__data");
        
        if (infiniteScrollData && nextPage !== totalPages) {
          const nextUrlInput = infiniteScrollData.querySelector("input[data-next-url]");
          const currentPageInput = infiniteScrollData.querySelector("input[data-current-page]");
          
          if (nextUrlInput) {
            nextUrlInput.dataset.nextUrl = urlForUpdate;
            nextUrlInput.value = urlForUpdate;
          }
          
          if (currentPageInput) {
            currentPageInput.dataset.currentPage = nextPage;
            currentPageInput.value = nextPage;
          }
          
          checkVisibility();
        }
        
        r.removeAttribute("disabled");
        r.classList.remove("loading");
        
        // Remove button if we've reached the last page
        if (nextPage === totalPages) {
          r.remove();
        }
      }
    });
}

// ============================================
// INTERSECTION OBSERVER FOR AUTO-LOAD
// ============================================

function checkVisibility() {
  document.querySelectorAll(".js-infinite-scroll").forEach(button => {
    let observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          loadItems(button);
          observer.unobserve(entry.target);
        }
      });
    });
    
    observer.observe(button);
  });
}

// ============================================
// MANUAL LOAD MORE BUTTON
// ============================================

function loadMore() {
  document.querySelectorAll(".js-load-more").forEach(button => {
    button.onclick = () => {
      loadItems(button);
    };
  });
  
  checkVisibility();
}

// ============================================
// EVENT LISTENERS
// ============================================

// Save state before user navigates away
window.addEventListener('beforeunload', () => {
  saveState();
});

// Save state periodically as user scrolls
let scrollTimeout;
window.addEventListener('scroll', () => {
  clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    // Only save if we're beyond page 1
    const currentPage = document.querySelector("[data-current-page]");
    if (currentPage && parseInt(currentPage.value, 10) > 1) {
      saveState();
    }
  }, 500);
});

// Handle browser back/forward buttons
window.addEventListener('popstate', (event) => {
  // Force reload on popstate to get fresh content
  // The restore will happen on the load event
  location.reload();
});

// Restore state when page loads
window.addEventListener('load', () => {
  const restored = restoreState();
  
  // If state wasn't restored, initialize normally
  if (!restored) {
    loadMore();
  } else {
    // State was restored, reinitialize buttons
    loadMore();
  }
});

// ============================================
// INITIALIZE ON DOM READY (fallback)
// ============================================

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const restored = restoreState();
    if (!restored) {
      loadMore();
    } else {
      loadMore();
    }
  });
} else {
  // DOM is already ready
  const restored = restoreState();
  if (!restored) {
    loadMore();
  } else {
    loadMore();
  }
}